<template>
  <div class="vehicle-readings" v-if="vehicle">

    <v-row dense class="mb-4" align="center">
      <v-col cols="auto" style="min-width: 260px">
        <date-picker
            :label="$t('period')"
            range
            outlined
            persistent-placeholder
            clearable
            v-model="filters.dates"
            hide-details
        />
      </v-col>
      <v-col cols="auto">
        <v-select
            v-model="filters.source"
            :items="sourceOptions"
            :label="$t('vehicle.source')"
            clearable
            hide-details
            outlined
            dense
            style="min-width: 180px"
        />
      </v-col>
      <v-col cols="auto">
        <v-select
            v-model="filters.triggerOrigin"
            :items="triggerOriginOptions"
            :label="$t('vehicle.trigger-origin')"
            clearable
            hide-details
            outlined
            dense
            style="min-width: 180px"
        />
      </v-col>
      <v-col cols="auto">
        <v-select
            v-model="filters.provider"
            :items="providerOptions"
            :label="$t('vehicle.provider')"
            clearable
            hide-details
            outlined
            dense
            style="min-width: 170px"
        />
      </v-col>
      <v-col cols="auto">
        <v-btn small @click="search">
          {{ $t('search') }}
        </v-btn>
      </v-col>
    </v-row>

    <!-- Volteras-style telemetry curve: pick a metric, compare providers over time. One series per provider
         (or a single one when the provider filter is set) so the dense (Volteras) vs sparse (SmartCar) capture
         is visible at a glance. -->
    <v-card outlined class="mb-4">
      <v-card-text>
        <div class="d-flex align-center flex-wrap mb-2" style="gap: 12px">
          <v-btn-toggle v-model="selectedMetric" mandatory dense class="flex-wrap">
            <v-btn v-for="m in chartMetrics" :key="m.key" :value="m.key" small>{{ $t('vehicle.chart.' + m.key) }}</v-btn>
          </v-btn-toggle>
          <v-spacer />
          <!-- Time window, Volteras-style: the custom date-picker above wins when set, otherwise these shortcuts. -->
          <v-btn-toggle v-model="chartRange" mandatory dense>
            <v-btn v-for="r in rangeOptions" :key="r.value" :value="r.value" small>{{ $t('vehicle.chart.window.' + r.value) }}</v-btn>
          </v-btn-toggle>
          <span v-if="!chartLoading" class="caption grey--text">{{ chartPointCount }} {{ $t('vehicle.chart.points') }}</span>
        </div>
        <div v-if="chartLoading" class="text-center py-8">
          <v-progress-circular indeterminate color="primary" />
        </div>
        <highcharts v-else-if="chartSeries.length" :options="chartOptions" :callback="onMainChart" />
        <!-- Dedicated, time-synced OFFLINE track: ONE row/legend entry per operator so offline zones can be toggled
             BY OPERATOR via the native legend (instead of cluttering the curve with un-filterable plot bands). -->
        <template v-if="chartSeries.length && hasEventData">
          <div class="caption grey--text text--darken-1 mt-2 mb-n1">{{ $t('vehicle.chart.events-track') }}</div>
          <highcharts :options="eventChartOptions" :callback="onTrackChart" />
        </template>
        <v-alert v-else-if="!chartSeries.length" type="info" text dense class="mb-0">{{ $t('vehicle.chart.no-data') }}</v-alert>
      </v-card-text>
    </v-card>

    <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="total"
    >
      <!-- Header help: the reading date is when the VEHICLE measured the data (OEM timestamp), which can be
           older than when we collected it — an idle car reports the same measurement for hours. -->
      <template #header.readingAt="{header}">
        {{ header.text }}
        <v-tooltip bottom max-width="300">
          <template #activator="{on}"><v-icon x-small v-on="on" class="ml-1 ra-help">mdi-information-outline</v-icon></template>
          <span>{{ $t('vehicle.reading-at-help') }}</span>
        </v-tooltip>
      </template>

      <template #item.readingAt="{item}">
        <!-- Two consistent lines: the measurement time (when the VEHICLE measured it, from the OEM) always on
             top, the collection time (when WE fetched it) always below — "collecté à HH:mm" when it lags, or
             "en temps réel" when fetched right away. Both lines carry an icon so the meaning is unambiguous. -->
        <div class="ra-main">
          <!-- Online/offline at capture: an offline reading is a frozen stale value the reconstruction skips. -->
          <v-icon v-if="item.vehicleConnected === false" x-small color="red darken-1" class="mr-1" :title="$t('vehicle.reading-offline')">mdi-lan-disconnect</v-icon>
          <v-icon v-else-if="item.vehicleConnected === true" x-small color="green darken-1" class="mr-1" :title="$t('vehicle.reading-online')">mdi-lan-connect</v-icon>
          <v-icon x-small class="mr-1 ra-ic">mdi-car-outline</v-icon>{{ formatDate(item.readingAt, $t('format_datetime')) }}
        </div>
        <div class="ra-sub">
          <v-icon x-small class="mr-1">mdi-download-outline</v-icon>{{ collectedLabel(item) }}
        </div>
      </template>

      <!-- Dynamic measures: one chip per metric the reading ACTUALLY carries. A thin Volteras webhook shows a
           single chip (e.g. SoC); a rich SmartCar reading shows several. Rows are never blank. -->
      <template #item.measures="{item}">
        <div class="m-cell" v-if="measures(item).length">
          <template v-for="m in measures(item)">
            <div v-if="m.key === 'soc'" :key="m.key" class="m-chip m-chip--soc">
              <v-icon x-small class="mr-1">mdi-battery-70</v-icon>
              <span>{{ m.value.toFixed(1) }} %</span>
              <span class="m-bar-track"><span class="m-bar" :style="{ width: m.value + '%', background: batteryColor(m.value) }"></span></span>
            </div>
            <v-tooltip v-else :key="m.key" bottom>
              <template #activator="{ on }">
                <div v-on="on" class="m-chip" :class="'m-chip--' + m.tone">
                  <v-icon x-small class="mr-1">{{ m.icon }}</v-icon>
                  <span>{{ m.text }}</span>
                  <span v-if="m.delta" class="m-delta">+{{ formatMileage(m.delta) }}</span>
                </div>
              </template>
              <span>{{ m.label }}</span>
            </v-tooltip>
          </template>
        </div>
        <span v-else class="m-empty">—</span>
      </template>

      <template #item.source="{item}">
        <v-chip small label :color="getSourceColor(item.source)" class="source-chip">
          {{ $t('vehicle.source-types.' + item.source) }}
        </v-chip>
      </template>

      <template #item.triggerOrigin="{item}">
        <v-tooltip bottom>
          <template v-slot:activator="{ on }">
            <span v-on="on" class="trigger-text">
              {{ $t('vehicle.trigger-types.' + item.triggerOrigin) }}
            </span>
          </template>
          <span>{{ item.triggerOrigin }}</span>
        </v-tooltip>
      </template>

      <template #item.provider="{item}">
        <v-chip small outlined>
          <v-icon small left>{{ getProviderIcon(item.provider) }}</v-icon>
          {{ item.provider }}
        </v-chip>
      </template>

      <template #item.actions="{item}">
        <v-btn icon small @click="openPayload(item)" :title="$t('vehicle.view-payload')">
          <v-icon small>mdi-eye-outline</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- Full payload: every field the reading stored, for when the chips aren't enough (debug / thin webhooks). -->
    <v-dialog v-model="payloadDialog" max-width="560">
      <v-card v-if="selected">
        <v-card-title class="text-subtitle-1">
          {{ $t('vehicle.reading-detail-title') }}
          <v-spacer />
          <v-btn icon @click="payloadDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-card-title>

        <v-card-text>
          <div class="pd-head mb-3">
            <div><v-icon x-small class="mr-1 ra-ic">mdi-car-outline</v-icon>{{ formatDate(selected.readingAt, $t('format_datetime')) }}</div>
            <div class="pd-head__collected">
              <v-icon x-small class="mr-1">mdi-download-outline</v-icon>{{ collectedLabel(selected) }}
            </div>
            <div class="pd-head__meta">
              <v-chip x-small label :color="getSourceColor(selected.source)">{{ $t('vehicle.source-types.' + selected.source) }}</v-chip>
              <span>{{ $t('vehicle.trigger-types.' + selected.triggerOrigin) }}</span>
              <v-chip x-small outlined><v-icon x-small left>{{ getProviderIcon(selected.provider) }}</v-icon>{{ selected.provider }}</v-chip>
            </div>
          </div>

          <table class="pd-table">
            <tr v-for="row in payloadRows(selected)" :key="row.key">
              <td class="pd-key">{{ row.key }}</td>
              <td class="pd-val">{{ row.value }}</td>
            </tr>
          </table>
          <div v-if="!payloadRows(selected).length" class="grey--text text-center py-4">
            {{ $t('vehicle.no-payload') }}
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

  </div>
</template>

<script>
import VehicleRepository from "@repository/VehicleRepository";
import dayjs from "dayjs";
import Highcharts from "highcharts";
import xrange from "highcharts/modules/xrange";

// xrange = horizontal time bars, used for the per-operator offline / missing-charge track below the curve.
if (!Highcharts.seriesTypes.xrange) {
  xrange(Highcharts);
}

export default {
  data() {
    return {
      items: [],
      total: 0,
      filters: {
        dates: null,
        source: null,
        triggerOrigin: null,
        provider: null,
      },
      options: {
        sortBy: ['readingAt'],
        sortDesc: [true],
      },
      payloadDialog: false,
      selected: null,
      // Chart: one full fetch of readings (all metrics carried), the metric switch picks the field client-side.
      chartReadings: [],
      chartLoading: false,
      selectedMetric: 'soc',
      chartRange: 'd1',
      rangeOptions: [
        { value: 'd1', amount: 1, unit: 'day' },
        { value: 'w1', amount: 1, unit: 'week' },
        { value: 'm1', amount: 1, unit: 'month' },
        { value: 'y1', amount: 1, unit: 'year' },
        { value: 'all', amount: null },
      ],
      // Metric → the reading field it plots (+ unit, optional value transform). Kept in trust order.
      // Metric → the reading field it plots (+ unit, optional value transform, optional despike jump threshold).
      metricDefs: [
        { key: 'soc', field: 'batteryLevelPercent', unit: '%', min: 0, max: 100, spikeJump: 12 },
        { key: 'energy', field: 'batteryEnergyKwh', unit: 'kWh', spikeJump: 8 },
        { key: 'added', field: 'chargeEnergyAddedKwh', unit: 'kWh' },
        { key: 'power', field: 'chargeWattage', unit: 'kW', transform: w => Math.round(w / 10) / 100 },
        { key: 'range', field: 'batteryRangeKm', unit: 'km', spikeJump: 60 },
        { key: 'odometer', field: 'mileageKm', unit: 'km' },
      ],
      spikeMaxDurationMs: 15 * 60 * 1000,
      spikeRevertTolerance: 3,
      providerColors: { SMARTCAR: '#2563EB', VOLTERAS: '#059669', LINK_BY_CAR: '#D97706', ENODE: '#7C3AED' },
    }
  },

  watch: {
    options() {
      this.loadData()
    },
    // Keep the metric selection valid as the available metrics change with the data.
    chartMetrics(list) {
      if (list.length && !list.some(m => m.key === this.selectedMetric)) {
        this.selectedMetric = list[0].key
      }
    },
    // Changing the time window refetches the chart (the table keeps its own date filter).
    chartRange() {
      this.loadChart()
    },
  },

  mounted() {
    // Non-reactive holders for the two Highcharts instances (never put chart objects in reactive data).
    this._charts = { main: null, track: null }
    this.loadChart()
  },

  computed: {
    vehicle() {
      return this.$parent.vehicle ?? null
    },

    headers() {
      return [
        { text: this.$t('vehicle.reading-at'), value: 'readingAt' },
        { text: this.$t('vehicle.measures-col'), value: 'measures', sortable: false },
        { text: this.$t('vehicle.source'), value: 'source', sortable: false },
        { text: this.$t('vehicle.trigger-origin'), value: 'triggerOrigin', sortable: false },
        { text: this.$t('vehicle.provider'), value: 'provider', sortable: false },
        { text: '', value: 'actions', sortable: false, align: 'end' },
      ]
    },

    sourceOptions() {
      return [
        { text: this.$t('vehicle.source-types.TELEMATICS_POLL'), value: 'TELEMATICS_POLL' },
        { text: this.$t('vehicle.source-types.CHARGE_START'), value: 'CHARGE_START' },
        { text: this.$t('vehicle.source-types.CHARGE_STOP'), value: 'CHARGE_STOP' },
        { text: this.$t('vehicle.source-types.MANUAL'), value: 'MANUAL' },
      ]
    },

    triggerOriginOptions() {
      return [
        { text: this.$t('vehicle.trigger-types.PROVIDER_WEBHOOK'), value: 'PROVIDER_WEBHOOK' },
        { text: this.$t('vehicle.trigger-types.CATCH_UP'), value: 'CATCH_UP' },
        { text: this.$t('vehicle.trigger-types.BACKFILL'), value: 'BACKFILL' },
        { text: this.$t('vehicle.trigger-types.JOB'), value: 'JOB' },
        { text: this.$t('vehicle.trigger-types.MANUAL'), value: 'MANUAL' },
      ]
    },

    // Real-time webhooks are authoritative: for a provider that streams them, its CATCH_UP poll can serve a
    // STALE, frozen snapshot (Volteras) that contradicts the live value and makes the curve oscillate. Drop
    // those poll readings so the chart shows the true curve. Webhook-less providers (LBC/BACKFILL, SmartCar/JOB)
    // are untouched — they carry no PROVIDER_WEBHOOK reading.
    cleanChartReadings() {
      const webhookProviders = new Set(
          this.chartReadings.filter(r => r.triggerOrigin === 'PROVIDER_WEBHOOK').map(r => r.provider),
      )
      return this.chartReadings.filter(r => !(webhookProviders.has(r.provider) && r.triggerOrigin === 'CATCH_UP'))
    },

    // Providers actually present in the fetched data (drives the filter + the per-provider series).
    providersInData() {
      return [...new Set(this.cleanChartReadings.map(r => r.provider).filter(Boolean))].sort()
    },

    providerOptions() {
      return this.providersInData.map(p => ({ text: p, value: p }))
    },

    // Only the metrics that at least one reading carries — no empty tab.
    chartMetrics() {
      return this.metricDefs.filter(m => this.cleanChartReadings.some(r => r[m.field] != null))
    },

    chartPointCount() {
      const def = this.metricDefs.find(m => m.key === this.selectedMetric)
      if (!def) return 0
      const providers = this.filters.provider ? [this.filters.provider] : this.providersInData
      return this.cleanChartReadings.filter(r => r[def.field] != null && providers.includes(r.provider)).length
    },

    // One line per provider (or just the filtered one) for the selected metric, sorted by time.
    chartSeries() {
      const def = this.metricDefs.find(m => m.key === this.selectedMetric)
      if (!def) return []
      const providers = this.filters.provider ? [this.filters.provider] : this.providersInData
      return providers.map(provider => {
        let data = this.cleanChartReadings
            .filter(r => r.provider === provider && r[def.field] != null)
            .map(r => [new Date(r.readingAt).getTime(), def.transform ? def.transform(r[def.field]) : r[def.field]])
            .sort((a, b) => a[0] - b[0])
        if (def.spikeJump) {
          data = this.despikeSeries(data, def.spikeJump)
        }
        return {
          name: provider,
          data,
          color: this.providerColors[provider] || '#6B7280',
          marker: { enabled: data.length < 80 },
        }
      }).filter(s => s.data.length > 0)
    },

    chartOptions() {
      const def = this.metricDefs.find(m => m.key === this.selectedMetric) || {}
      return {
        chart: { height: 420, zoomType: 'x', events: this.legendHoverBinder() },
        time: { useUTC: false }, // show local time, consistent with the table (toLocaleString)
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: true },
        // Offline/missing zones no longer clutter the curve as plot bands — they live in the synced track below,
        // where each operator is its own legend-toggleable series. Both charts share the same time extent.
        xAxis: { type: 'datetime', min: this.timeExtent.min, max: this.timeExtent.max },
        yAxis: {
          title: { text: def.unit || null },
          min: def.min ?? null,
          max: def.max ?? null,
        },
        tooltip: { xDateFormat: '%d/%m %H:%M', valueSuffix: ' ' + (def.unit || ''), shared: false },
        // Hovering an operator's line also highlights that operator's row in the track below (and dims the rest).
        plotOptions: { series: { events: this.crossHoverEvents('track') } },
        series: this.chartSeries,
      }
    },

    // The exact time window of the curve, shared with the track below so the two line up pixel-for-pixel.
    timeExtent() {
      const ts = this.cleanChartReadings.map(r => new Date(r.readingAt).getTime()).filter(t => !isNaN(t))
      return ts.length ? { min: Math.min(...ts), max: Math.max(...ts) } : { min: null, max: null }
    },

    // The dedicated OFFLINE track: ONE row + ONE legend series per operator, so the chart's native legend filters
    // the offline zones BY OPERATOR (impossible with plot bands, which the legend can't toggle). Each operator's
    // silences (a gap > 1 h in its own readings) sit in their own lane instead of overlapping on the curve.
    // Respects the top provider filter. (Suspected missing charges have their own per-operator track on the meter.)
    eventTrack() {
      const GAP_MS = 60 * 60 * 1000
      const base = this.filters.provider ? [this.filters.provider] : this.providersInData
      const categories = []
      const series = []
      for (const provider of base) {
        const color = this.providerColors[provider] || '#9e9e9e'
        const data = []
        const times = this.cleanChartReadings
            .filter(r => r.provider === provider)
            .map(r => new Date(r.readingAt).getTime())
            .sort((a, b) => a - b)
        for (let i = 1; i < times.length; i++) {
          if (times[i] - times[i - 1] > GAP_MS) {
            data.push({ x: times[i - 1], x2: times[i], color: this.hexToRgba(color, 0.5) })
          }
        }
        if (!data.length) {
          continue
        }
        const idx = categories.length
        data.forEach(d => { d.y = idx })
        categories.push(provider)
        series.push({ name: provider, type: 'xrange', color, pointWidth: 12, data })
      }
      return { categories, series }
    },

    hasEventData() {
      return this.eventTrack.series.length > 0
    },

    eventChartOptions() {
      const providers = this.eventTrack.categories
      const offlineLabel = this.$t('vehicle.chart.offline')
      return {
        chart: { type: 'xrange', height: 70 + providers.length * 38, zoomType: 'x', events: this.legendHoverBinder() },
        time: { useUTC: false },
        title: { text: null },
        credits: { enabled: false },
        legend: { enabled: true },
        xAxis: { type: 'datetime', min: this.timeExtent.min, max: this.timeExtent.max },
        yAxis: { title: { text: null }, categories: providers, reversed: true, min: 0, max: Math.max(0, providers.length - 1) },
        tooltip: {
          headerFormat: '',
          pointFormatter() {
            const f = Highcharts.dateFormat
            return `<b>${this.series.name}</b><br>${offlineLabel}<br>${f('%d/%m %H:%M', this.x)} → ${f('%H:%M', this.x2)}`
          },
        },
        plotOptions: {
          xrange: { borderRadius: 2, borderWidth: 0, minPointLength: 3 },
          // Hovering an operator's row also highlights its line in the curve above (and dims the rest).
          series: { events: this.crossHoverEvents('main') },
        },
        series: this.eventTrack.series,
      }
    },
  },

  methods: {
    // Capture each Highcharts instance so hovering one can drive the other (cross-chart operator highlight).
    onMainChart(chart) {
      this._charts.main = chart
    },
    onTrackChart(chart) {
      this._charts.track = chart
    },

    // Series hover handlers that mirror the highlight onto the SIBLING chart: hovering operator X here puts X's
    // series in the other chart into 'hover' and every other series into 'inactive' (dimmed) — so hovering Enode
    // in the curve lights up Enode's row in the track, and vice versa. `this` inside the handlers is the series.
    crossHoverEvents(siblingKey) {
      const self = this
      return {
        mouseOver() { self.highlightAcross(siblingKey, this.name, true) },
        mouseOut() { self.highlightAcross(siblingKey, this.name, false) },
      }
    },

    highlightAcross(siblingKey, name, on) {
      const chart = this._charts && this._charts[siblingKey]
      if (!chart || !chart.series) {
        return
      }
      chart.series.forEach(s => s.setState(on ? (s.name === name ? 'hover' : 'inactive') : ''))
    },

    // Highlight an operator in BOTH charts at once (used from legend hover, which natively highlights neither).
    highlightBoth(name, on) {
      this.highlightAcross('main', name, on)
      this.highlightAcross('track', name, on)
    },

    // chart.events.load/redraw handler factory: after each render, attach DOM hover listeners to every legend item
    // (Highcharts fires no series event on legend hover), so hovering an operator's legend name highlights it in
    // both charts. Legend items are rebuilt on redraw → rebind then; a per-element flag avoids double-binding.
    legendHoverBinder() {
      const self = this
      const bind = function () { self.bindLegendHover(this) }
      return { load: bind, redraw: bind }
    },

    bindLegendHover(chart) {
      if (!chart || !chart.series) {
        return
      }
      chart.series.forEach(s => {
        const el = (s.legendGroup && s.legendGroup.element) || (s.legendItem && s.legendItem.element)
        if (!el || el._raHoverBound) {
          return
        }
        el._raHoverBound = true
        el.style.cursor = 'pointer'
        el.addEventListener('mouseenter', () => this.highlightBoth(s.name, true))
        el.addEventListener('mouseleave', () => this.highlightBoth(s.name, false))
      })
    },

    // Build the chip list from whatever metrics are present on the reading (all optional per provider).
    measures(item) {
      const m = []
      if (item.mileageKm != null) {
        m.push({ key: 'odometer', tone: 'odometer', icon: 'mdi-counter', text: this.formatMileage(item.mileageKm) + ' km', delta: item.mileageDeltaKm, label: this.$t('vehicle.measures.odometer') })
      }
      if (item.batteryLevelPercent != null) {
        m.push({ key: 'soc', value: item.batteryLevelPercent })
      }
      if (item.batteryRangeKm != null) {
        m.push({ key: 'range', tone: 'range', icon: 'mdi-map-marker-distance', text: this.formatMileage(item.batteryRangeKm) + ' km', label: this.$t('vehicle.measures.range') })
      }
      if (item.chargingStatus || item.isCharging != null) {
        m.push({ key: 'charge', tone: 'charge', icon: 'mdi-ev-station', text: item.chargingStatus || (item.isCharging ? this.$t('vehicle.measures.charging') : this.$t('vehicle.measures.not-charging')), label: this.$t('vehicle.measures.charge-state') })
      }
      if (item.isPluggedIn != null) {
        m.push({ key: 'plug', tone: 'plug', icon: item.isPluggedIn ? 'mdi-power-plug' : 'mdi-power-plug-off', text: item.isPluggedIn ? this.$t('vehicle.measures.plugged') : this.$t('vehicle.measures.unplugged'), label: this.$t('vehicle.measures.plug') })
      }
      if (item.chargeWattage != null) {
        m.push({ key: 'power', tone: 'power', icon: 'mdi-flash', text: (item.chargeWattage / 1000).toFixed(1) + ' kW', label: this.$t('vehicle.measures.power') })
      }
      if (item.chargeEnergyAddedKwh != null) {
        m.push({ key: 'added', tone: 'added', icon: 'mdi-battery-charging', text: '+' + item.chargeEnergyAddedKwh.toFixed(1) + ' kWh', label: this.$t('vehicle.measures.energy-added') })
      }
      if (item.batteryEnergyKwh != null) {
        m.push({ key: 'energy', tone: 'energy', icon: 'mdi-battery', text: item.batteryEnergyKwh.toFixed(1) + ' kWh', label: this.$t('vehicle.measures.battery-energy') })
      }
      return m
    },

    // The measurement time (readingAt, from the OEM) and the collection time (createdAt) differ meaningfully
    // when we re-fetched a frozen measurement (idle car). Below a minute they're effectively the same event,
    // so we don't clutter the row with a redundant collection line.
    collectedDiffers(item) {
      if (!item || !item.createdAt || !item.readingAt) return false
      return Math.abs(dayjs(item.createdAt).diff(dayjs(item.readingAt), 'second')) > 60
    },

    // The collection sub-line, always shown: "collecté en temps réel" when we fetched it right after the
    // vehicle measured it, otherwise "collecté à HH:mm" (adding the date only when it's a different day).
    collectedLabel(item) {
      if (!this.collectedDiffers(item)) {
        return this.$t('vehicle.collected-realtime')
      }
      const collected = dayjs(item.createdAt)
      const sameDay = collected.isSame(dayjs(item.readingAt), 'day')
      const when = this.formatDate(item.createdAt, this.$t(sameDay ? 'format_time' : 'format_datetime'))
      return this.$t('vehicle.collected-at') + ' ' + when
    },

    // Raw stored snapshot as key/value rows (non-null only) for the payload dialog.
    payloadRows(item) {
      const snap = item.snapshot || {}
      return Object.keys(snap)
          .filter(k => snap[k] !== null && snap[k] !== undefined && snap[k] !== '')
          .map(k => ({ key: k, value: this.formatPayloadValue(snap[k]) }))
    },

    formatPayloadValue(v) {
      if (typeof v === 'boolean') return v ? this.$t('yes') : this.$t('no')
      return v
    },

    openPayload(item) {
      this.selected = item
      this.payloadDialog = true
    },

    search() {
      if (this.options.page > 1) {
        this.options.page = 1
      } else {
        this.loadData()
      }
      this.loadChart()
    },

    // Drop transient revert-spikes (Volteras glitch bursts): a value that jumps by ≥ jump then returns to its
    // pre-jump baseline within spikeMaxDurationMs is removed. A real charge never reverts, so it is kept.
    despikeSeries(data, jump) {
        const keep = data.map(() => true)
        let i = 1
        while (i < data.length) {
            const baseline = data[i - 1][1]
            if (data[i][1] - baseline >= jump) {
                let j = i
                while (j + 1 < data.length && data[j + 1][1] >= baseline + jump - this.spikeRevertTolerance) {
                    j++
                }
                const reverts = j + 1 < data.length
                    && data[j + 1][1] <= baseline + this.spikeRevertTolerance
                    && (data[j + 1][0] - data[i][0]) <= this.spikeMaxDurationMs
                if (reverts) {
                    for (let k = i; k <= j; k++) keep[k] = false
                    i = j + 1
                    continue
                }
            }
            i++
        }
        return data.filter((_, idx) => keep[idx])
    },

    // One unfiltered-by-provider fetch of the whole period: the provider filter then just toggles which
    // series show (client-side), so switching provider/metric is instant with no refetch.
    loadChart() {
      if (!this.vehicle) return

      const data = { limit: -1, orderBy: 'readingAt', orderDirection: 'asc' }
      if (this.filters.dates) {
        // A custom range chosen in the date-picker wins over the shortcut buttons.
        data.dateFrom = dayjs(this.filters.dates[0], 'YYYY-MM-DD').format('YYYY-MM-DD HH:mm:ss')
        data.dateTo = dayjs(this.filters.dates[1], 'YYYY-MM-DD').format('YYYY-MM-DD 23:59:59')
      } else {
        const range = this.rangeOptions.find(r => r.value === this.chartRange)
        if (range && range.amount) {
          data.dateFrom = dayjs().subtract(range.amount, range.unit).format('YYYY-MM-DD HH:mm:ss')
        }
      }

      this.chartLoading = true
      VehicleRepository.readings(this.vehicle.id, data).then(response => {
        this.chartReadings = response.items || []
        this.chartLoading = false
      }).catch(() => {
        this.chartReadings = []
        this.chartLoading = false
      })
      // Missing charges are NOT shown here anymore — they live on the charge-sessions page (per provider, in red),
      // where they belong. The readings chart keeps only the per-provider offline bands.
    },

    formatMileage(value) {
      if (value == null) return '-'
      return value.toLocaleString()
    },

    batteryColor(level) {
      if (level >= 60) return '#059669'
      if (level >= 30) return '#D97706'
      return '#DC2626'
    },

    getSourceColor(source) {
      const map = {
        'TELEMATICS_POLL': '#E8F5E9',
        'CHARGE_START': '#E3F2FD',
        'CHARGE_STOP': '#FFF3E0',
        'MANUAL': '#F3E5F5',
      }
      return map[source] || '#F5F5F5'
    },

    getProviderIcon(provider) {
      const map = {
        'SMARTCAR': 'mdi-car-connected',
        'GEOTAB': 'mdi-router-wireless',
      }
      return map[provider] || 'mdi-cloud'
    },

    // Translucent fill/border from a provider's hex colour, so each provider's bands are told apart.
    hexToRgba(hex, alpha) {
      const h = hex.replace('#', '')
      const r = parseInt(h.substring(0, 2), 16)
      const g = parseInt(h.substring(2, 4), 16)
      const b = parseInt(h.substring(4, 6), 16)
      return 'rgba(' + r + ', ' + g + ', ' + b + ', ' + alpha + ')'
    },

    loadData() {
      if (!this.vehicle) return

      let data = {}

      if (this.filters.dates) {
        data.dateFrom = dayjs(this.filters.dates[0], 'YYYY-MM-DD').format('YYYY-MM-DD HH:mm:ss')
        data.dateTo = dayjs(this.filters.dates[1], 'YYYY-MM-DD').format('YYYY-MM-DD 23:59:59')
      }

      if (this.filters.source) {
        data.source = this.filters.source
      }

      if (this.filters.triggerOrigin) {
        data.triggerOrigin = this.filters.triggerOrigin
      }

      if (this.filters.provider) {
        data.provider = this.filters.provider
      }

      if (this.options.itemsPerPage > -1) {
        data.offset = (this.options.page * this.options.itemsPerPage) - this.options.itemsPerPage
      }
      data.limit = this.options.itemsPerPage

      if (this.options.sortBy.length > 0) {
        data.orderBy = this.options.sortBy[0]
      }
      if (this.options.sortDesc.length > 0) {
        data.orderDirection = this.options.sortDesc[0] ? 'desc' : 'asc'
      }

      this.showLoading(true)
      VehicleRepository.readings(this.vehicle.id, data).then(response => {
        this.items = response.items
        this.total = response.total
        this.showLoading(false)
      }).catch(err => {
        this.notifyError(err)
        this.showLoading(false)
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.m-cell {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 6px 0;
}

.m-chip {
  display: inline-flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  padding: 5px 9px;
  border-radius: 8px;
  white-space: nowrap;
  color: #374151;
  background: #F3F4F6;

  &--odometer { background: #EFF6FF; color: #1D4ED8; }
  &--soc      { background: #ECFDF5; color: #047857; }
  &--range    { background: #F0FDFA; color: #0F766E; }
  &--charge   { background: #FFF7ED; color: #C2410C; }
  &--plug     { background: #F5F3FF; color: #6D28D9; }
  &--power    { background: #FEFCE8; color: #A16207; }
  &--added    { background: #FDF4FF; color: #A21CAF; }
  &--energy   { background: #EEF2FF; color: #4338CA; }
}

.m-delta {
  margin-left: 5px;
  font-size: 10.5px;
  font-weight: 600;
  color: #059669;
  background: rgba(5, 150, 105, 0.12);
  padding: 1px 5px;
  border-radius: 6px;
}

.m-bar-track {
  display: inline-block;
  width: 34px;
  height: 5px;
  border-radius: 3px;
  margin-left: 6px;
  background: rgba(4, 120, 87, 0.15);
  overflow: hidden;
}
.m-bar { display: block; height: 100%; border-radius: 3px; }

.m-empty { color: #cbd5e1; }

.ra-main { font-size: 13px; font-weight: 600; display: flex; align-items: center; white-space: nowrap; }
.ra-ic { color: #94a3b8; }
.ra-sub {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #94a3b8;
  margin-top: 2px;
  white-space: nowrap;
}
.ra-help { color: #cbd5e1; cursor: help; }
.pd-head__collected {
  display: flex;
  align-items: center;
  font-size: 11.5px;
  color: #94a3b8;
  margin-top: 2px;
}

.source-chip {
  font-weight: 600;
  font-size: 11px !important;
}

.trigger-text {
  font-size: 12px;
  color: #6B7280;
  cursor: help;
}

.pd-head {
  font-weight: 600;
  &__meta {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 12px;
    color: #6B7280;
    font-weight: 500;
    margin-top: 4px;
  }
}

.pd-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  td { padding: 6px 8px; border-bottom: 1px solid #F1F5F9; vertical-align: top; }
  .pd-key { color: #6B7280; white-space: nowrap; width: 45%; }
  .pd-val { font-weight: 600; word-break: break-word; }
}
</style>
